import React, { useState } from 'react'

import {
  DownloadMiniIcon,
  Flex,
  FullScreenSpinner,
  isMobile,
  makeStyles,
  paddingPx,
  theme,
  useMediaQuery,
  ZoomInIcon,
  ZoomOutIcon,
} from '@guiker/react-framework'

import { Document, Page } from './react-pdf'

type PdfViewerProps = {
  assetUrl: string
  width?: string
  height?: string
  withDownload?: boolean
  scale?: number
}

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
  },
  container: {
    width: ({ width }: { width: string }) => (width ? width : '100%'),
    maxWidth: theme.dimensions.pageLayout.maxWidth.desktop,
    background: theme.palette.grey[5],
    overflowY: 'scroll',
    overflowX: 'auto',
    height: ({ height }: { height: string }) => (height ? height : '65vh'),
    borderWidth: 1,
    borderColor: theme.palette.primary.main,
    borderStyle: 'solid',
    justifyContent: 'center',
    alignContent: 'center',
    [isMobile]: {
      height: ({ height }: { height: string }) => (height ? height : '85vh'),
    },
  },
  controls: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  zoomIn: {
    padding: paddingPx(6, 6, 2, 6),
  },
  zoomOut: {
    borderLeft: `1px solid ${theme.palette.grey[30]}`,
    padding: paddingPx(6, 6, 2, 6),
  },
})

export const PdfViewer: React.FC<PdfViewerProps> = ({ assetUrl, width, height, withDownload, scale = 1.5 }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [totalPages, setTotalPages] = useState(0)
  const [zoomScale, setZoomScale] = useState(scale)
  const classes = useStyles({ width, height })
  const onDownloadClicked = () => typeof window !== 'undefined' && window.open(assetUrl)

  const documentOnLoadSuccess = ({ numPages }: { numPages: number }) => {
    setTotalPages(numPages)
  }

  const handleZoom = (zoom: number) => {
    if (zoom <= 4 && zoom >= 0.5) setZoomScale(zoom)
  }

  if (!assetUrl) return <></>

  return (
    <div className={classes.wrapper}>
      <Flex className={classes.container}>
        <Document file={assetUrl} onLoadSuccess={documentOnLoadSuccess} loading={<FullScreenSpinner />}>
          <Flex flexDirection='column' gap={1}>
            {Array.from(new Array(totalPages), (_, index) => (
              <Page break scale={zoomScale} key={`page_${index + 1}`} pageNumber={index + 1} />
            ))}
          </Flex>
        </Document>
      </Flex>
      <Flex className={classes.controls}>
        <div onClick={() => handleZoom(zoomScale - (isMobile ? 0.25 : 0.5))} className={classes.zoomIn}>
          <ZoomOutIcon color={10} />
        </div>
        <div onClick={() => handleZoom(scale + (isMobile ? 0.25 : 0.5))} className={classes.zoomOut}>
          <ZoomInIcon color={10} />
        </div>
        {withDownload && (
          <div onClick={onDownloadClicked} className={classes.zoomOut}>
            <DownloadMiniIcon color={10} />
          </div>
        )}
      </Flex>
    </div>
  )
}
